import { inject, Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { InstellingBron, RRol } from '@cumlaude/service-contract';
import { UserService } from './user.service';
import { UserRouteData, UserRoutesService } from './user-routes.service';
import { RouteDef } from '@cumlaude/shared-components-menu';

@Injectable({
	providedIn: 'root',
})
export class AutorisatieService {
	readonly router = inject(Router);
	readonly userService = inject(UserService);
	readonly routesService = inject(UserRoutesService);

	/**
	 * Checkt of een gebruiker bij een bepaalde url mag komen.
	 * Geeft true of false terug.
	 */
	isUrlAllowed(urlRequested: string): boolean {
		const result = this.checkUrlForActiveRol(urlRequested);
		return result instanceof UrlTree ? true : result;
	}

	isUrlAllowedForRollen(urlRequested: string, rollen: RRol[]): boolean {
		const authorisedRoutes = this.routesService.findAuthorisedRoutesForRollen(rollen);
		const result = this.checkAllowedForUrl(urlRequested, authorisedRoutes);
		return result instanceof UrlTree ? true : result;
	}

	/**
	 * Checkt of een gebruiker bij een bepaalde url mag komen.
	 * Geeft true of false terug indien de gebruiker hier wel of niet bij mag komen.
	 * Indien alleen bij specifieke suburls, geeft dan een UrlTree terug naar die specifieke suburl.
	 */
	checkUrlForActiveRol(urlRequested: string): boolean | UrlTree {
		if (this.isAllowedWithoutRol(urlRequested)) return true;
		if (this.isBeheerAllowed(urlRequested)) return true;

		const authorisedRoutes = this.routesService.activeAuthorisedRoutes();
		return this.checkAllowedForUrl(urlRequested, authorisedRoutes);
	}

	/**
	 * Zonder actieve rol mag je bij root, de startpagina en de gedeelde pagina's.
	 */
	private isAllowedWithoutRol(urlRequested: string): boolean {
		return urlRequested.startsWith('/start') || urlRequested.startsWith('/shared') || urlRequested == '/' || urlRequested == '';
	}

	/**
	 * Als je de rol beheerder hebt toegewezen gekregen, mag je bij alles dat onder /beheer valt.
	 */
	private isBeheerAllowed(urlRequested: string) {
		if (!urlRequested.startsWith('/beheer')) return false;

		const rollen = this.userService.rollen();
		if (!rollen?.includes(RRol.BEHEERDER)) return false;

		const bron = this.userService.bron();
		const account = this.userService.account();
		const vso = this.userService.vso();

		if (urlRequested.startsWith('/beheer/support')) return account?.support;
		else if (urlRequested.startsWith('/beheer/cijferinstellingen')) return bron === InstellingBron.Magister;
		else if (urlRequested.startsWith('/beheer/vestiging-cluster-vso')) return vso;
		return true;
	}

	/**
	 * Haalt de slash aan het begin weg, splits de url vervolgens in stukjes en start daarna het checken van de url.
	 */
	private checkAllowedForUrl(urlRequested: string, routes: RouteDef<UserRouteData>[]): boolean | UrlTree {
		const urlParts = urlRequested.substring(1).split('/');
		return this.checkUrlAllowed(urlRequested, urlParts, routes);
	}

	/**
	 * Doet een aantal checks op de url om te controleren of de gebruiker de url mag gebruiken.
	 *  1. Is het eerste deel van de url sowieso toegestaan voor de gebruiker.
	 *  2. Check of de gebruiker's school matched met het gewenste onderwijstype bij specifieke domeinen.
	 *  3. Checkt of de url toestemming gaat over de alle suburl's of alleen specifieke suburl's.
	 *  4. Checkt of we nu onderaan de url zijn en toch nog suburl's als opties hebben, zo ja, redirecten naar eerste suburl.
	 *  5. Vervolgt bij de volgende url deel met checks.
	 */
	private checkUrlAllowed(urlRequested: string, urlParts: string[], routes: RouteDef<UserRouteData>[]): boolean | UrlTree {
		const [urlPart, query] = urlParts[0].split('?');
		const queryPart = query ? `?${query}` : '';

		const route = routes.find((part) => part.path == urlPart);
		if (!route) return false;

		if (!route.subRoutes) return true;

		if (urlParts.length == 1) {
			const dashboard = route.subRoutes[0].path;
			return this.router.parseUrl(`${urlRequested}/${dashboard}${queryPart}`);
		}

		return this.checkUrlAllowed(urlRequested, urlParts.slice(1), route.subRoutes);
	}
}
